import { defineStore } from 'pinia'
import type { components } from '~/api/api'
import client from '~/api/client'
import { useAuthStore } from '~/store/auth'

const STORAGE_KEY = 'qcms-selected-web-id'

type WebSummary = components['schemas']['WebSummary']

export const useWebStore = defineStore('web', () => {
  const websites = ref<WebSummary[]>([])
  const selectedWebsite = ref<WebSummary>()

  function setSelected(web: WebSummary | undefined) {
    selectedWebsite.value = web
    if (import.meta.client) {
      if (web) localStorage.setItem(STORAGE_KEY, String(web.id))
      else localStorage.removeItem(STORAGE_KEY)
    }
  }

  /** Select by id — used when a route carries ?webId= (e.g. jumping to a
   *  language mutation from the editor). Unknown ids are ignored so an admin
   *  editing someone else's web doesn't clear their own selection. */
  function selectById(id: number) {
    const found = websites.value.find((w) => w.id === id)
    if (found) setSelected(found)
    return found
  }

  /** Display name that disambiguates language mutations: several webs of one
   *  logical site share a title, so append the language code when the user has
   *  more than one mutation of the same `language_group`. */
  function labelFor(web: WebSummary | undefined) {
    if (!web) return ''
    const group = (web as any).language_group
    const siblings = group ? websites.value.filter((w) => (w as any).language_group === group) : []
    if (siblings.length < 2) return web.title
    return `${web.title} (${String((web as any).language ?? '').toUpperCase()})`
  }

  function syncFromAuth() {
    const auth = useAuthStore()
    websites.value = auth.webs as WebSummary[]
    if (!websites.value.length) {
      setSelected(undefined)
      return
    }
    const storedId = import.meta.client ? localStorage.getItem(STORAGE_KEY) : null
    const stored = storedId ? websites.value.find((w) => String(w.id) === storedId) : undefined
    setSelected(stored ?? websites.value[0])
  }

  async function fetchAdminWebs() {
    const { data } = await client.GET('/api/admin/webs/')
    if (!data) return []
    return data
  }

  return { websites, selectedWebsite, setSelected, selectById, labelFor, syncFromAuth, fetchAdminWebs }
})
